import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { MapPin, Phone, Mail } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// Social media SVG icons (lucide-react doesn't export these in v1.x)
const InstagramIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);
const TwitterIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4l16 16M4 20L20 4"/>
  </svg>
);
const FacebookIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
  </svg>
);
const LinkedinIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
  </svg>
);
const YoutubeIcon = ({ size = 15 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02"/>
  </svg>
);

const SOCIAL_ICONS = [
  { Icon: InstagramIcon, label: 'Instagram' },
  { Icon: TwitterIcon, label: 'X (Twitter)' },
  { Icon: FacebookIcon, label: 'Facebook' },
  { Icon: LinkedinIcon, label: 'LinkedIn' },
  { Icon: YoutubeIcon, label: 'YouTube' },
];


export default function Footer() {
  const cities = [
    { name: 'İstanbul', href: '/property-search?il=istanbul' },
    { name: 'Ankara', href: '/property-search?il=ankara' },
    { name: 'İzmir', href: '/property-search?il=izmir' },
    { name: 'Antalya', href: '/property-search?il=antalya' },
    { name: 'Muğla', href: '/property-search?il=mugla' },
    { name: 'Bursa', href: '/property-search?il=bursa' },
    { name: 'Eskişehir', href: '/property-search?il=eskisehir' },
    { name: 'Trabzon', href: '/property-search?il=trabzon' },
  ];

  const propertyTypes = [
    { name: 'Satılık Daire', href: '/property-search?tip=satilik&tur=daire' },
    { name: 'Kiralık Daire', href: '/property-search?tip=kiralik&tur=daire' },
    { name: 'Satılık Villa', href: '/property-search?tip=satilik&tur=villa' },
    { name: 'Kiralık Villa', href: '/property-search?tip=kiralik&tur=villa' },
    { name: 'Satılık Arsa', href: '/property-search?tip=satilik&tur=arsa' },
    { name: 'Satılık İşyeri', href: '/property-search?tip=satilik&tur=isyeri' },
    { name: 'Kiralık Ofis', href: '/property-search?tip=kiralik&tur=ofis' },
    { name: 'Satılık Müstakil', href: '/property-search?tip=satilik&tur=mustakil' },
  ];

  const services = [
    { name: 'İlan Ver', href: '/property-search' },
    { name: 'Emlakçı Bul', href: '/property-search?emlakci=true' },
    { name: 'Yatırım Analizi', href: '/property-search?yatirim=true' },
    { name: 'Kredi Hesapla', href: '/property-detail' },
    { name: 'Piyasa Analizi', href: '/property-search' },
    { name: 'AI Asistan', href: '/' },
    { name: 'Harita ile Ara', href: '/property-search?view=map' },
    { name: 'Fiyat Takibi', href: '/property-search' },
  ];

  const legal = [
    { name: 'Gizlilik Politikası', href: '/' },
    { name: 'Kullanım Koşulları', href: '/' },
    { name: 'KVKK Aydınlatma', href: '/' },
    { name: 'Çerez Politikası', href: '/' },
    { name: 'Yardım Merkezi', href: '/' },
    { name: 'İletişim', href: '/' },
  ];

  return (
    <footer className="bg-foreground text-white mt-20">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link href="/" className="flex items-center gap-2.5 mb-4">
              <AppLogo size={36} className="brightness-0 invert" />
              <span className="font-extrabold text-xl tracking-tight text-white">Evora</span>
            </Link>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              Türkiye&apos;nin AI destekli premium emlak platformu. Hayalinizdeki evi bulun, yatırımınızı analiz edin.
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-white/60">
                <MapPin size={14} className="text-accent flex-shrink-0" />
                <span>Levent, İstanbul, Türkiye</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Phone size={14} className="text-accent flex-shrink-0" />
                <span>0850 123 45 67</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/60">
                <Mail size={14} className="text-accent flex-shrink-0" />
                <span>destek@evora.com.tr</span>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              {SOCIAL_ICONS.map(({ Icon, label }) => (
                <Link
                  key={`social-${label}`}
                  href="/"
                  aria-label={label}
                  className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white/60 hover:bg-primary hover:text-white transition-all duration-150"
                >
                  <Icon size={15} />
                </Link>
              ))}
            </div>
          </div>

          {/* Cities */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Popüler Şehirler</h4>
            <ul className="space-y-2">
              {cities?.map((city) => (
                <li key={`footer-city-${city?.name}`}>
                  <Link href={city?.href} className="text-sm text-white/60 hover:text-white transition-colors">
                    {city?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Property Types */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">İlan Türleri</h4>
            <ul className="space-y-2">
              {propertyTypes?.map((type) => (
                <li key={`footer-type-${type?.name}`}>
                  <Link href={type?.href} className="text-sm text-white/60 hover:text-white transition-colors">
                    {type?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Hizmetler</h4>
            <ul className="space-y-2">
              {services?.map((service) => (
                <li key={`footer-service-${service?.name}`}>
                  <Link href={service?.href} className="text-sm text-white/60 hover:text-white transition-colors">
                    {service?.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wider">Yasal</h4>
            <ul className="space-y-2">
              {legal?.map((item) => (
                <li key={`footer-legal-${item?.name}`}>
                  <Link href={item?.href} className="text-sm text-white/60 hover:text-white transition-colors">
                    {item?.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-3 rounded-xl bg-white/5 border border-white/10">
              <p className="text-xs text-white/50 leading-relaxed">
                Tüm fiyat tahminleri ve yatırım analizleri yalnızca bilgilendirme amaçlıdır. Yatırım kararlarınızı uzman danışmanlarla değerlendirmenizi öneririz.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/40">
            © 2026 Evora Teknoloji A.Ş. Tüm hakları saklıdır.
          </p>
          <div className="flex items-center gap-6">
            <span className="text-xs text-white/30">Türkiye&apos;nin Akıllı Emlak Platformu</span>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse-soft"></div>
              <span className="text-xs text-white/40">Tüm sistemler çalışıyor</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}